/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Directive, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, Self} from '@angular/core';
import {NgModel} from '@angular/forms';
import * as _ from 'lodash';

import {formatIsoCurrency} from '../../../../components/util/iso-currency-format';
import {AppLocaleService} from '../../locale/app-locale.service';
import {LocalNetworkProfile} from '../../../../components/session/local-network-profile';

@Directive({
  standalone: false,
  selector: '[adnMoneySpacer]'
})
export class MoneySpacerDirective implements OnInit, OnDestroy {
  @Input() adnMoneySpacer: number | string;

  private formatTimer: ReturnType<typeof setTimeout>;

  constructor(
    @Self() private readonly ngModel: NgModel,
    private readonly elementRef: ElementRef<HTMLInputElement>,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly appLocale: AppLocaleService
  ) {
  }

  ngOnInit(): void {
    this.formatTimer = setTimeout(() => this.formatView(), 50);
  }

  ngOnDestroy(): void {
    clearTimeout(this.formatTimer);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const {decPlaces} = this.resolveCurrencyOrDec();
    if (event.ctrlKey || event.altKey || event.metaKey || typeof event.key !== 'string' || event.key.length !== 1) {
      return;
    }

    if (!_.isFinite(parseInt(event.key, 10)) && (decPlaces === 0 || event.key !== '.')) {
      event.preventDefault();
    }
  }

  @HostListener('focus')
  onFocus(): void {
    const modelValue = this.ngModel.model;
    this.elementRef.nativeElement.value = modelValue === null || modelValue === undefined ? '' : String(modelValue);
  }

  @HostListener('blur')
  onBlur(): void {
    this.formatView();
  }

  private resolveCurrencyOrDec(): {decPlaces: number | null; curr: string} {
    const value = this.adnMoneySpacer;
    const defaultCurrency = this.LocalNetworkProfile.get('defaultCurrency');

    if (_.isFinite(value)) {
      return {decPlaces: value as number, curr: defaultCurrency};
    }

    if (_.isString(value)) {
      const asNumber = Number(value);
      if (value.trim() !== '' && _.isFinite(asNumber)) {
        return {decPlaces: asNumber, curr: defaultCurrency};
      }
      return {decPlaces: null, curr: value};
    }

    return {decPlaces: null, curr: defaultCurrency};
  }

  private formatView(): void {
    const el = this.elementRef.nativeElement;
    if (!el.value.length) {
      return;
    }

    const {decPlaces, curr} = this.resolveCurrencyOrDec();
    const value = formatIsoCurrency(
      el.value,
      curr,
      decPlaces,
      true,
      true,
      this.LocalNetworkProfile.get('defaultCurrency'),
      this.appLocale.getLocaleId()
    );
    if (value) {
      el.value = value;
    }
  }
}
